import React, { useEffect } from 'react';
import { styled as styled2 } from '@compiled/react';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useActiveIncidentsForService } from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/controllers/index.tsx';
import type { ServiceDataLoadingState } from '@atlassian/jira-servicedesk-linked-object-incidents-store/src/controllers/types.tsx';
import IncidentList from './incident-list/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const Content = ({
	serviceData: { data, loading, error },
	serviceName,
}: {
	serviceData: ServiceDataLoadingState;
	serviceName?: string;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const activeIncidents = data?.activeIncidents ?? [];

	useEffect(() => {
		if (loading) {
			return;
		}

		if (error) {
			fireErrorAnalytics({
				meta: {
					id: 'activeIncidentPanel',
					packageName: 'jiraServicedeskObjectActiveIncidentsPanel',
				},
				attributes: {
					errorType: error,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		} else {
			fireTrackAnalytics(createAnalyticsEvent({}), 'activeIncidentPanel rendered', {
				numOfIncidents: activeIncidents.length,
			});
		}
	}, [createAnalyticsEvent, activeIncidents.length, loading, error]);

	if (error) {
		return (
			<Container>
				<IconWrapper>
					<ErrorIcon spacing="spacious" color={token('color.icon.danger')} label="error icon" />
				</IconWrapper>
				<ContentContainer>
					<Title>{formatMessage(messages.errorTitle)}</Title>
					<Description>{formatMessage(messages.errorDescription)}</Description>
				</ContentContainer>
			</Container>
		);
	}
	if (loading || activeIncidents.length === 0) {
		return null;
	}
	// We are trying to format the service name here so that:
	// * The name is wrapped inside apostrophes
	// * Keep the apostrophes when the name itself is truncated
	// * Don't break at the apostrophes when the name has to wrap in another line
	const title =
		serviceName !== undefined ? (
			<FormattedMessage
				{...messages.titleWithServicename}
				values={{
					incidentCount: activeIncidents.length,
					serviceName: (
						<ServiceNameWrapper>
							<FormattedMessage
								{...messages.serviceName}
								values={{ serviceName: <ServiceName>{serviceName}</ServiceName> }}
							/>
						</ServiceNameWrapper>
					),
				}}
			/>
		) : (
			formatMessage(messages.title, {
				incidentCount: activeIncidents.length,
			})
		);
	return (
		<Container data-testid="servicedesk-object-active-incidents-panel.ui.active-incident-panel.container">
			<IconWrapper>
				<WarningIcon spacing="spacious" color={token('color.icon.warning')} label="warning icon" />
			</IconWrapper>
			<ContentContainer>
				<Title>{title}</Title>
				<Description>
					<IncidentList
						incidents={activeIncidents.map(({ issueKey, issueSummary, issueTypeIconUrl }) => ({
							issueKey,
							issueSummary,
							issueTypeIconUrl,
						}))}
					/>
				</Description>
			</ContentContainer>
		</Container>
	);
};

const WrappedContent = ({ serviceId, serviceName, renderWrapper }: Props) => {
	const activeIncidentsState = useActiveIncidentsForService(serviceId);

	const content = Content({ serviceData: activeIncidentsState, serviceName });
	if (content === null) {
		return null;
	}

	return renderWrapper
		? renderWrapper({ hasError: activeIncidentsState.error !== null }, content)
		: content;
};

const ObjectActiveIncidentsPanel = (props: Props): React.JSX.Element => (
	<ContextualAnalyticsData sourceName="activeIncidents" sourceType={SCREEN}>
		<WrappedContent {...props} />
	</ContextualAnalyticsData>
);

export default ObjectActiveIncidentsPanel;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled2.div({
	flexGrow: 1,
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'* + &': {
		marginTop: token('space.100'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled2.div({
	flex: '0 0 auto',
	width: '40px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginTop: token('space.negative.025'),
		marginRight: 0,
		marginBottom: token('space.negative.025'),
		marginLeft: 0,
		verticalAlign: 'top',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ServiceNameWrapper = styled2.span({
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ServiceName = styled2.span({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	maxWidth: '400px',
	display: 'inline-block',
	verticalAlign: 'bottom',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.section({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});
Container.displayName = 'Container';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled2.h4({
	margin: 0,
	font: token('font.heading.xsmall'),
});

Title.displayName = 'Title';
